import { ShieldCheck } from "lucide-react";
import { ctaText, isProfileTemplate, templateMeta } from "./interstitialConfig";

// Preview estático da página intermediária. Espelha o visual do
// redirect-spectrum (static/interstitial.css) em escala reduzida.

const TG_PATH = "M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z";

const RING = {
    rosa: "linear-gradient(135deg, #ec4899, #ef4444)",
    azul: "linear-gradient(135deg, #38bdf8, #2563eb)",
    roxo: "linear-gradient(135deg, #c084fc, #7c3aed)",
    verde: "linear-gradient(135deg, #34d399, #059669)",
    escuro: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
    custom: "linear-gradient(135deg, #ec4899, #ef4444)",
};

function CtaButton({ label, loading, disabled, showIcon = true }) {
    return (
        <div
            className="w-full min-h-[40px] py-2.5 px-3 rounded-xl text-[13px] font-bold text-white flex items-center justify-center gap-2"
            style={{
                background: "linear-gradient(135deg, #0088cc, #00aaff)",
                boxShadow: disabled ? "none" : "0 4px 15px rgba(0,136,204,0.35)",
                opacity: disabled ? 0.55 : 1,
            }}
        >
            {loading ? (
                <>
                    <span className="w-4 h-4 rounded-full border-2 border-white/35 border-t-white animate-spin flex-shrink-0" />
                    <span className="truncate">Abrindo o Telegram...</span>
                </>
            ) : (
                <>
                    {showIcon && <svg data-tg-icon width="16" height="16" viewBox="0 0 24 24" fill="white" className="flex-shrink-0" aria-hidden="true"><path d={TG_PATH} /></svg>}
                    <span className="truncate">{label}</span>
                </>
            )}
        </div>
    );
}

function VerifiedBadge() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-label="Verificado" className="flex-shrink-0">
            <path fill="#3b82f6" d="M12 1l2.6 2.2 3.4-.4.9 3.3 3 1.7-1.2 3.2 1.2 3.2-3 1.7-.9 3.3-3.4-.4L12 23l-2.6-2.2-3.4.4-.9-3.3-3-1.7L3.3 13 2.1 9.8l3-1.7.9-3.3 3.4.4z" />
            <path d="M7.5 12.2l3 3 6-6.2" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ProfileCard({ config, avatarUrl }) {
    const dark = config.template === "escuro";
    const initial = (config.name || "?").trim().charAt(0).toUpperCase() || "?";
    return (
        <div
            className={`rounded-2xl p-5 text-center w-full backdrop-blur-xl ${dark ? "bg-[#141821]/85 border border-white/[0.08] text-white" : "bg-white/[0.88] text-gray-800"}`}
            style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}
        >
            <div className="w-20 h-20 mx-auto mb-3 rounded-full p-[3px]" style={{ background: RING[config.template] || RING.rosa }}>
                <div className={`w-full h-full rounded-full border-2 overflow-hidden flex items-center justify-center text-2xl font-bold ${dark ? "border-[#141821] bg-gray-800 text-white/60" : "border-white bg-gray-200 text-gray-500"}`}>
                    {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : initial}
                </div>
            </div>
            {(config.name || config.verified) && (
                <div className="flex items-center justify-center gap-1 mb-1 min-w-0">
                    {config.name && <span className="text-base font-extrabold truncate">{config.name}</span>}
                    {config.verified && <VerifiedBadge />}
                </div>
            )}
            <div className="flex items-center justify-center gap-1.5 mb-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-bold text-green-500">Online</span>
            </div>
            {config.response_time && (
                <p className={`text-[11px] mb-4 ${dark ? "text-white/55" : "text-gray-500"}`}>
                    Tempo médio de resposta: {config.response_time}
                </p>
            )}
            <CtaButton label={ctaText(config)} loading={config.auto_redirect} showIcon={config.show_telegram_icon !== false} />
        </div>
    );
}

function DarkCard({ children }) {
    return (
        <div className="rounded-2xl p-5 text-center w-full bg-[#141821]/85 border border-white/[0.08] text-white" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.45)" }}>
            {children}
        </div>
    );
}

function InteractiveCard({ config }) {
    const label = ctaText(config);
    switch (config.template) {
        case "raspadinha":
            return (
                <div className="w-full">
                    <div
                        className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden mb-3 border border-white/[0.1] flex flex-col items-center justify-center gap-0.5 text-center"
                        style={{
                            background: "linear-gradient(135deg, #e4e4e7 0%, #a1a1aa 45%, #d4d4d8 55%, #8f8f99 100%)",
                            boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
                        }}
                    >
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#52525b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-1.5" aria-hidden="true">
                            <path d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" />
                        </svg>
                        <div className="text-sm font-extrabold text-[#3f3f46]">Raspe para ver</div>
                        <div className="text-[10px] text-[#52525b]">Use o dedo para descobrir</div>
                    </div>
                </div>
            );
        case "verificacao":
            return (
                <DarkCard>
                    <ShieldCheck size={40} className="mx-auto mb-2 text-[#60a5fa]" strokeWidth={1.8} />
                    <div className="text-base font-extrabold mb-1">Verificação de segurança</div>
                    <div className="text-[11px] text-white/60 mb-3">Confirme que você não é um robô para continuar</div>
                    <div className="flex items-center gap-3 p-3 rounded-xl border border-white/[0.12] bg-white/[0.04] text-left">
                        <span className="w-5 h-5 rounded-md border-2 border-white/35 flex-shrink-0" />
                        <span className="text-sm font-semibold">Não sou um robô</span>
                    </div>
                </DarkCard>
            );
        case "countdown":
            return (
                <DarkCard>
                    <div className="text-base font-extrabold mb-1">Seu acesso está sendo liberado</div>
                    <div className="text-[11px] text-white/60 mb-3">Aguarde alguns segundos</div>
                    <div className="relative w-24 h-24 mx-auto mb-3">
                        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                            <defs>
                                <linearGradient id="sx-prev-ring" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0" stopColor="#a78bfa" /><stop offset="1" stopColor="#06b6d4" />
                                </linearGradient>
                            </defs>
                            <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
                            <circle cx="60" cy="60" r="52" fill="none" stroke="url(#sx-prev-ring)" strokeWidth="8" strokeLinecap="round" strokeDasharray="327" strokeDashoffset="80" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center text-3xl font-extrabold">{config.countdown_seconds}</div>
                    </div>
                    <CtaButton label={`Aguarde ${config.countdown_seconds}s`} disabled />
                </DarkCard>
            );
        case "mais18":
            return (
                <DarkCard>
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full border-[3px] border-[#ef4444] flex items-center justify-center text-xl font-black" style={{ boxShadow: "0 0 30px rgba(239,68,68,0.35)" }}>18+</div>
                    <div className="text-base font-extrabold mb-1">Conteúdo para maiores de 18 anos</div>
                    <div className="text-[11px] text-white/60 mb-3">Este conteúdo é restrito. Confirme sua idade para continuar.</div>
                    <CtaButton label={label} />
                    <div className="mt-2 py-2 rounded-xl border border-white/[0.12] text-[13px] font-semibold text-white/70">Sair</div>
                </DarkCard>
            );
        case "presente":
        default:
            return (
                <div className="text-center w-full">
                    <div className="relative w-28 h-28 mx-auto mb-4">
                        <div className="absolute top-[-14px] left-1/2 -translate-x-1/2 text-2xl z-[3]">🎀</div>
                        <div className="absolute top-0 left-[-6px] w-[124px] h-9 rounded-lg z-[2]" style={{ background: "linear-gradient(135deg, #f43f5e, #ec4899)" }}>
                            <div className="absolute left-1/2 -translate-x-1/2 w-4 h-full" style={{ background: "linear-gradient(180deg, #fbbf24, #f59e0b)" }} />
                        </div>
                        <div className="absolute bottom-0 w-28 h-20 rounded-xl overflow-hidden" style={{ background: "linear-gradient(135deg, #ec4899, #f43f5e)", boxShadow: "0 10px 30px rgba(236,72,153,0.3)" }}>
                            <div className="absolute left-1/2 -translate-x-1/2 w-4 h-full" style={{ background: "linear-gradient(180deg, #fbbf24, #f59e0b)" }} />
                            <div className="absolute top-1/2 -translate-y-1/2 w-full h-4" style={{ background: "linear-gradient(90deg, #fbbf24, #f59e0b)" }} />
                        </div>
                    </div>
                    <div className="text-sm font-semibold text-[#c084fc] mb-1">Toque para abrir</div>
                    <div className="text-[11px] text-[#c084fc]/55">Você recebeu um presente exclusivo</div>
                </div>
            );
    }
}

export default function InterstitialPreview({ config, avatarUrl }) {
    const meta = templateMeta(config.template);
    const bg = config.template === "custom" ? config.custom_color : meta.bg;
    return (
        <div className="rounded-xl border border-white/[0.06] bg-[#0a0a0a] p-3 md:p-4">
            <div
                className="rounded-2xl overflow-hidden w-full max-w-[280px] mx-auto aspect-[9/16] flex items-center justify-center p-4"
                style={{ background: bg }}
            >
                {isProfileTemplate(config.template)
                    ? <ProfileCard config={config} avatarUrl={avatarUrl} />
                    : <InteractiveCard config={config} />}
            </div>
        </div>
    );
}
